import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getQuotes, deleteQuote, convertQuoteToInvoice, getQuotePdfUrl } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  FileText, Plus, Search, MoreVertical, Eye, Trash2,
  ArrowRightCircle, Download, X as XIcon, Users,
  Send, CheckCircle2, Clock, AlertTriangle, Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import InvoiceDesignBanner from '../../components/invoices/InvoiceDesignBanner';
import AiCreateButton from '../../components/ai/AiCreateButton';
import PageHelpButton from '../../components/help/PageHelpButton';
import { StatCard, FilterChip, FilterRow, FiltersCard, SurfaceCard, ResetFiltersButton } from '../../components/ui/filters-toolkit';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(amount || 0);

const STATUS_LABELS = {
  draft: 'Borrador', sent: 'Enviado', accepted: 'Aceptado',
  rejected: 'Rechazado', expired: 'Expirado',
};

const STATUS_CHIPS = [
  { value: 'all', label: 'Todos', dot: 'bg-stone-400' },
  { value: 'draft', label: 'Borradores', dot: 'bg-stone-400' },
  { value: 'sent', label: 'Enviados', dot: 'bg-blue-500' },
  { value: 'accepted', label: 'Aceptados', dot: 'bg-emerald-500' },
  { value: 'rejected', label: 'Rechazados', dot: 'bg-rose-500' },
  { value: 'expired', label: 'Expirados', dot: 'bg-amber-500' },
];

const getYear = (iso) => {
  if (!iso) return null;
  const y = parseInt(iso.slice(0, 4), 10);
  return Number.isFinite(y) ? y : null;
};

const StatusBadge = ({ status }) => (
  <span className={`status-badge status-${status}`}>{STATUS_LABELS[status] || status}</span>
);

export const QuotesPage = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getQuotes({});
        if (!cancelled) setQuotes(data);
      } catch {
        toast.error('Error al cargar presupuestos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const reload = async () => {
    try {
      const { data } = await getQuotes({});
      setQuotes(data);
    } catch {
      toast.error('Error al recargar');
    }
  };

  const years = useMemo(() => {
    const set = new Set(quotes.map((q) => getYear(q.issue_date)).filter(Boolean));
    return Array.from(set).sort((a, b) => b - a);
  }, [quotes]);

  const clients = useMemo(() => {
    const set = new Set(quotes.map((q) => q.client_name).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [quotes]);

  const filteredQuotes = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    return quotes.filter((quote) => {
      if (q) {
        const hay = `${quote.quote_number || ''} ${quote.client_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter !== 'all' && quote.status !== statusFilter) return false;
      if (yearFilter !== 'all' && getYear(quote.issue_date) !== parseInt(yearFilter, 10)) return false;
      if (clientFilter !== 'all' && quote.client_name !== clientFilter) return false;
      return true;
    });
  }, [quotes, searchTerm, statusFilter, yearFilter, clientFilter]);

  const stats = useMemo(() => {
    const acc = { total: 0, accepted: 0, pending: 0, expired: 0, count: filteredQuotes.length, acceptedCount: 0 };
    filteredQuotes.forEach((q) => {
      const t = Number(q.total) || 0;
      acc.total += t;
      if (q.status === 'accepted') { acc.accepted += t; acc.acceptedCount += 1; }
      else if (q.status === 'sent' || q.status === 'draft') acc.pending += t;
      else if (q.status === 'expired') acc.expired += t;
    });
    return acc;
  }, [filteredQuotes]);

  const activeFiltersCount = [
    statusFilter !== 'all',
    yearFilter !== 'all',
    clientFilter !== 'all',
    searchTerm.trim() !== '',
  ].filter(Boolean).length;

  const resetFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setYearFilter('all'); setClientFilter('all');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este presupuesto?')) return;
    try { await deleteQuote(id); toast.success('Presupuesto eliminado'); reload(); }
    catch { toast.error('Error al eliminar presupuesto'); }
  };

  const handleConvertToInvoice = async (id) => {
    try { await convertQuoteToInvoice(id); toast.success('Presupuesto convertido a factura'); reload(); }
    catch { toast.error('Error al convertir presupuesto'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Presupuestos</h1>
            <p className="text-stone-500 text-sm mt-1">Gestiona tus presupuestos</p>
          </div>
          <PageHelpButton moduleKey="quotes" variant="icon" />
        </div>
        <div className="flex gap-2 w-full sm:w-auto flex-wrap">
          <Link to="/quotes/new" className="w-full sm:w-auto">
            <Button className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto" data-testid="create-quote-button">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Presupuesto
            </Button>
          </Link>
          <AiCreateButton entity="quote" className="w-full sm:w-auto" />
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="quote-stats">
        <StatCard
          label="Presupuestado"
          value={formatCurrency(stats.total)}
          sub={`${stats.count} presup.${stats.count === 1 ? '' : 's'}`}
          icon={Receipt}
          gradient="bg-gradient-to-br from-orange-50 to-amber-50"
          iconBg="bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24]"
          testid="stat-quotes-total"
        />
        <StatCard
          label="Aceptado"
          value={formatCurrency(stats.accepted)}
          sub={stats.total > 0 ? `${Math.round((stats.accepted / stats.total) * 100)}% del total` : '—'}
          icon={CheckCircle2}
          gradient="bg-gradient-to-br from-emerald-50 to-teal-50"
          iconBg="bg-gradient-to-br from-emerald-500 to-teal-500"
          testid="stat-quotes-accepted"
        />
        <StatCard
          label="Pendiente"
          value={formatCurrency(stats.pending)}
          sub="Borradores + enviados"
          icon={Clock}
          gradient="bg-gradient-to-br from-sky-50 to-blue-50"
          iconBg="bg-gradient-to-br from-sky-500 to-blue-500"
          testid="stat-quotes-pending"
        />
        <StatCard
          label="Expirado"
          value={formatCurrency(stats.expired)}
          sub="Sin respuesta"
          icon={AlertTriangle}
          gradient="bg-gradient-to-br from-rose-50 to-red-50"
          iconBg="bg-gradient-to-br from-rose-500 to-red-500"
          testid="stat-quotes-expired"
        />
      </div>

      <InvoiceDesignBanner entityLabel="presupuesto" />

      {/* Filter card */}
      <FiltersCard testid="quote-filters-card">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
            <Input
              placeholder="Buscar por número o cliente…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white"
              data-testid="search-quotes-input"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-stone-200 hover:bg-stone-300 flex items-center justify-center text-stone-600">
                <XIcon className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="relative lg:w-64">
            <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none z-10" />
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="pl-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white" data-testid="quote-client-filter">
                <SelectValue placeholder="Todos los clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los clientes</SelectItem>
                {clients.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {activeFiltersCount > 0 && (
            <ResetFiltersButton count={activeFiltersCount} onClick={resetFilters} testid="quote-reset-filters" />
          )}
        </div>

        {years.length > 0 && (
          <FilterRow label="Año" testid="quote-year-filters">
            <FilterChip active={yearFilter === 'all'} onClick={() => setYearFilter('all')} testid="quote-year-chip-all">Todos</FilterChip>
            {years.map((y) => (
              <FilterChip key={y} active={yearFilter === String(y)} onClick={() => setYearFilter(String(y))} testid={`quote-year-chip-${y}`}>{y}</FilterChip>
            ))}
          </FilterRow>
        )}

        <FilterRow label="Estado" testid="quote-status-filters">
          {STATUS_CHIPS.map((s) => (
            <FilterChip key={s.value} active={statusFilter === s.value} onClick={() => setStatusFilter(s.value)} dot={s.dot} testid={`quote-status-chip-${s.value}`}>
              {s.label}
            </FilterChip>
          ))}
        </FilterRow>
      </FiltersCard>

      {/* Table */}
      {filteredQuotes.length > 0 ? (
        <SurfaceCard>
          <div className="overflow-x-auto">
            <table className="w-full invoice-table">
              <thead>
                <tr>
                  <th className="text-left px-4 py-3">Número</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Fecha</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Válido hasta</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-right px-4 py-3">Total</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredQuotes.map((quote) => (
                  <tr key={quote.id} className="hover:bg-orange-50/40 transition-colors" data-testid={`quote-row-${quote.id}`}>
                    <td className="px-4 py-4"><span className="font-mono text-sm text-[#FF6B4A]">{quote.quote_number}</span></td>
                    <td className="px-4 py-4 text-sm">{quote.client_name}</td>
                    <td className="px-4 py-4 text-sm text-stone-500 hidden md:table-cell">{quote.issue_date}</td>
                    <td className="px-4 py-4 text-sm text-stone-500 hidden md:table-cell">{quote.valid_until}</td>
                    <td className="px-4 py-4"><StatusBadge status={quote.status} /></td>
                    <td className="px-4 py-4 text-right font-mono text-sm font-medium">{formatCurrency(quote.total)}</td>
                    <td className="px-4 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 hover:bg-stone-100 rounded-xl" data-testid={`quote-menu-${quote.id}`}>
                            <MoreVertical className="w-4 h-4 text-stone-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/quotes/${quote.id}`} className="flex items-center gap-2"><Eye className="w-4 h-4" /> Ver</Link>
                          </DropdownMenuItem>
                          {quote.status !== 'accepted' && (
                            <DropdownMenuItem onClick={() => handleConvertToInvoice(quote.id)}>
                              <ArrowRightCircle className="w-4 h-4 mr-2" /> Convertir a Factura
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => window.open(getQuotePdfUrl(quote.id), '_blank')}>
                            <Download className="w-4 h-4 mr-2" /> Descargar PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(quote.id)} className="text-red-600 focus:text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SurfaceCard>
      ) : (
        <SurfaceCard testid="quotes-empty-state">
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <h3 className="text-lg font-medium text-stone-900 mb-1">
              {activeFiltersCount > 0 ? 'Sin resultados para los filtros aplicados' : 'No hay presupuestos'}
            </h3>
            <p className="text-stone-500 mb-4">
              {activeFiltersCount > 0 ? 'Prueba a limpiar algún filtro o ajustar la búsqueda.' : 'Crea tu primer presupuesto'}
            </p>
            {activeFiltersCount > 0 ? (
              <button onClick={resetFilters} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 font-semibold text-sm transition-all">
                <XIcon className="w-4 h-4" /> Limpiar filtros
              </button>
            ) : (
              <Link to="/quotes/new">
                <Button className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300" data-testid="create-first-quote-button">
                  <Plus className="w-4 h-4 mr-2" /> Crear Presupuesto
                </Button>
              </Link>
            )}
          </div>
        </SurfaceCard>
      )}
    </div>
  );
};

export default QuotesPage;
